import { useState, useRef, useEffect } from 'react';
import { Course } from '../../../mocks/courses';
import CoursePaymentModal from './CoursePaymentModal';

interface Props {
  course: Course;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const CourseSidebar = ({ course }: Props) => {
  const [showModal, setShowModal] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 3, hours: 0, minutes: 0, seconds: 0 });
  const sharePanelRef = useRef<HTMLDivElement>(null);

  const parseDiscountDeadline = (value?: string) => {
    if (!value) {
      return null;
    }
    const normalized = value.includes('T') ? value : value.replace(' ', 'T');
    const target = new Date(normalized);
    if (Number.isNaN(target.getTime())) {
      return null;
    }
    return target.getTime();
  };

  // 实时倒计时（后端配置截止时间）
  useEffect(() => {
    const deadline = parseDiscountDeadline(course.discountEndTime);
    if (!deadline) {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return undefined;
    }

    const tick = () => {
      const diff = deadline - Date.now();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [course.discountEndTime]);

  const pad = (n: number) => String(n).padStart(2, '0');

  const hasDiscount = Number(course.originalPrice) > Number(course.price) && Number(course.price) > 0;
  const discountPct = hasDiscount
    ? Math.max(0, Math.round((1 - course.price / course.originalPrice) * 100))
    : 0;
  const isFreeCourse = Number(course.price) <= 0;
  const hasMemberPrice = Number(course.memberPrice ?? 0) > 0 && Number(course.memberPrice) < Number(course.price);
  const totalLessons = course.chapters?.reduce((acc, ch) => acc + ch.lessons.length, 0) ?? 0;
  const courseUrl = `${window.location.origin}/course/${course.id}`;

  const includeIconPool = [
    'ri-play-circle-line',
    'ri-tools-line',
    'ri-smartphone-line',
    'ri-infinity-line',
    'ri-award-line',
    'ri-file-list-3-line',
  ];
  const includes = (course.courseIncludes?.length ? course.courseIncludes : []).map((label, idx) => ({
    icon: includeIconPool[idx % includeIconPool.length],
    label,
  }));

  const defaultCourseInfo = [
    { icon: 'ri-bar-chart-line', label: '難度級別', value: course.level },
    { icon: 'ri-book-open-line', label: '總章節數', value: `${course.chapters?.length ?? 0} 章節` },
    { icon: 'ri-file-list-3-line', label: '總課節數', value: `${totalLessons} 節課` },
    { icon: 'ri-time-line', label: '總學習時長', value: course.duration },
    { icon: 'ri-translate-2', label: '授課語言', value: course.language ?? '中文' },
    { icon: 'ri-refresh-line', label: '最近更新', value: course.lastUpdated ?? '–' },
  ];
  const courseInfoRows = (course.courseInfo?.length
    ? course.courseInfo.map((item) => ({ icon: 'ri-information-line', label: item.label, value: item.value }))
    : defaultCourseInfo);

  // 点击外部关闭分享面板
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (sharePanelRef.current && !sharePanelRef.current.contains(e.target as Node)) {
        setShowSharePanel(false);
      }
    };
    if (showSharePanel) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSharePanel]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(courseUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const input = document.createElement('input');
      input.value = courseUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleShareLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(courseUrl)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(`推薦課程：${course.title}`);
    const body = encodeURIComponent(`我在 NexusGo 發現了一門不錯的課程，推薦你看看！\n\n${course.title}\n${course.subtitle}\n\n課程連結：${courseUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div>
      {/* Price card */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-4">
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-3xl font-bold text-[#0A1628]">{isFreeCourse ? '免費' : `$${course.price}`}</span>
            {hasDiscount ? (
              <>
                <span className="text-lg text-slate-300 line-through">${course.originalPrice}</span>
                <span className="text-sm font-bold text-rose-500 bg-rose-50 px-2 py-0.5 rounded-full">
                  -{discountPct}%
                </span>
              </>
            ) : null}
          </div>
          {hasMemberPrice ? (
            <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-600">
              <i className="ri-vip-crown-fill" />
              會員價 ${course.memberPrice}
            </div>
          ) : null}

          {/* 限时优惠倒计时 */}
          {hasDiscount ? (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-time-line text-amber-500 text-xs" />
                <span className="text-amber-600 text-[11px] font-bold">限時優惠價 · 倒計時結束恢復原價</span>
              </div>
              {course.discountEndTime ? (
                <div className="flex items-center gap-1.5">
                  {[
                    { value: pad(timeLeft.days), unit: '天' },
                    { value: pad(timeLeft.hours), unit: '時' },
                    { value: pad(timeLeft.minutes), unit: '分' },
                    { value: pad(timeLeft.seconds), unit: '秒' },
                  ].map((item, idx) => (
                    <div key={item.unit} className="flex items-center gap-1.5">
                      <div className="flex flex-col items-center">
                        <span className="bg-amber-500 text-white text-sm font-black px-2 py-0.5 rounded-md min-w-[28px] text-center tabular-nums">
                          {item.value}
                        </span>
                        <span className="text-amber-400 text-[9px] mt-0.5">{item.unit}</span>
                      </div>
                      {idx < 3 && (
                        <span className="text-amber-400 font-bold text-sm mb-3">:</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-amber-600 text-xs">優惠進行中</p>
              )}
            </div>
          ) : null}

          {enrolled ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <i className="ri-check-line text-emerald-600 text-xl" />
              </div>
              <p className="text-emerald-600 font-semibold text-sm">已成功報名！</p>
              <p className="text-slate-400 text-xs mt-1">課程存取連結已傳送至你的信箱</p>
            </div>
          ) : (
            <button
              onClick={() => setShowModal(true)}
              className="w-full py-3 bg-violet-600 text-white font-bold text-sm rounded-xl cursor-pointer hover:bg-violet-700 active:scale-95 transition-all whitespace-nowrap"
            >
              立即報名
            </button>
          )}
        </div>

        {includes.length ? (
          <div className="px-5 py-4">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">課程包含</h4>
            <div className="space-y-2.5">
              {includes.map((item) => (
                <div key={item.label} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-violet-500">
                    <i className={`${item.icon} text-sm`} />
                  </div>
                  <span className="text-sm text-slate-500">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {/* Quick stats */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4">
        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">課程資訊</h4>
        <div className="space-y-3">
          {courseInfoRows.map((item) => (
            <div key={item.label} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-slate-400">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-xs`} />
                </div>
                {item.label}
              </div>
              <span className="font-semibold text-[#0A1628] text-xs">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Share / Gift buttons */}
      <div className="relative flex gap-2" ref={sharePanelRef}>
        <button
          onClick={() => setShowSharePanel(!showSharePanel)}
          className={`w-full py-2.5 bg-white border text-xs font-semibold rounded-xl cursor-pointer transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
            showSharePanel
              ? 'border-violet-400 text-violet-600 bg-violet-50'
              : 'border-slate-200 text-slate-500 hover:border-violet-200 hover:text-violet-600'
          }`}
        >
          <i className="ri-share-line" />
          分享課程
        </button>

        {/* Share panel dropdown */}
        {showSharePanel && (
          <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-slate-200 rounded-2xl overflow-hidden z-20">
            <div className="px-4 pt-4 pb-3 border-b border-slate-100">
              <p className="text-xs font-bold text-[#0A1628] mb-2">複製課程連結</p>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={courseUrl}
                  className="flex-1 min-w-0 px-3 py-2 text-[11px] bg-slate-50 border border-slate-200 rounded-lg text-slate-500 focus:outline-none"
                />
                <button
                  onClick={handleCopyLink}
                  className={`px-3 py-2 text-xs font-bold rounded-lg cursor-pointer transition-all whitespace-nowrap flex items-center gap-1.5 ${
                    copied
                      ? 'bg-emerald-500 text-white'
                      : 'bg-violet-600 text-white hover:bg-violet-700'
                  }`}
                >
                  {copied ? (
                    <><i className="ri-check-line" />已複製</>
                  ) : (
                    <><i className="ri-file-copy-line" />複製</>
                  )}
                </button>
              </div>
            </div>

            <div className="px-4 py-3">
              <p className="text-[11px] text-slate-400 mb-2.5">分享到</p>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { icon: 'ri-wechat-line', label: '微信', color: 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100', action: handleCopyLink },
                  { icon: 'ri-weibo-line', label: '微博', color: 'text-rose-500 bg-rose-50 hover:bg-rose-100', action: handleCopyLink },
                  { icon: 'ri-linkedin-box-line', label: 'LinkedIn', color: 'text-sky-600 bg-sky-50 hover:bg-sky-100', action: handleShareLinkedIn },
                  { icon: 'ri-mail-line', label: '電子郵件', color: 'text-slate-600 bg-slate-100 hover:bg-slate-200', action: handleShareEmail },
                ].map((s) => (
                  <button
                    key={s.label}
                    onClick={s.action}
                    className={`flex flex-col items-center gap-1 py-2 rounded-xl cursor-pointer transition-all ${s.color}`}
                  >
                    <i className={`${s.icon} text-base`} />
                    <span className="text-[10px] font-medium">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showModal && (
        <CoursePaymentModal
          course={course}
          onClose={() => setShowModal(false)}
          onSuccess={() => setEnrolled(true)}
        />
      )}
    </div>
  );
};

export default CourseSidebar;
